// Link targets offered by the reusable page-link picker (the Button block's
// link field, and any future link fields): the tenant's own pages plus the
// fixed pages added by active modules, each resolved to the site-relative URL
// the published template serves.

import type { ApiClient } from "./api";

export interface PageLinkOption {
  /** Site-relative URL, e.g. "/", "/about/", "/menu/". */
  url: string;
  label: string;
  /** False for draft portal pages — the link 404s until the page is published. */
  published: boolean;
}

interface PageRow {
  slug: string;
  title: string;
  published?: number;
}

// Site pages contributed by modules, offered while the module is active.
// Events, bookings and the restaurant menu render as in-page blocks, so they
// add no page here.
const MODULE_PAGES: Array<{ module: string; url: string; label: string }> = [
  { module: "posts", url: "/blog/", label: "Blog" },
];

/** The published site URL for a portal page slug. */
export function pageUrl(slug: string): string {
  return slug === "home" ? "/" : `/${slug}/`;
}

/** All pickable link targets: portal pages first, then active module pages. */
export async function fetchLinkOptions(
  api: ApiClient,
  activeModules: string[]
): Promise<PageLinkOption[]> {
  const data = await api.request<{ pages?: PageRow[] }>("/tenant/pages");
  const pages: PageLinkOption[] = (data.pages ?? []).map((page) => ({
    url: pageUrl(page.slug),
    label: page.title || pageUrl(page.slug),
    published: Boolean(page.published),
  }));
  const modulePages: PageLinkOption[] = MODULE_PAGES.filter((entry) =>
    activeModules.includes(entry.module)
  ).map((entry) => ({ url: entry.url, label: entry.label, published: true }));
  return [...pages, ...modulePages];
}
